import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  type SubdomainAssessmentRow,
  type WhanauProfileResponse,
} from '../api/client';
import { POUS, type Pou } from '../framework';
import { StateIcon } from './StateIcon';
import './WhanauShareView.css';
import { formatDate } from '../api/formatDate';

/**
 * WhanauShareView — modal overlay for handing the tablet to the whānau.
 *
 * Same pattern as the assessment wizard: centred card with a blurred
 * backdrop, rendered via createPortal to document.body so nothing of
 * the kaimahi profile is visible behind it (other than as a blurred
 * silhouette).
 *
 * Step-by-step flow:
 *   1. Welcome (name, whakataukī, cycle statement)
 *   2-5. One per Pou with that Pou's subdomains
 *   6. Tō whānau (members)
 *
 * Dismissal: ESC, backdrop click, close button, or the explicit
 * "Hand back to kaimahi" nav button. PIN protection (proper locked
 * mode) is the next session.
 */

const STATE_SCORE: Record<string, number> = {
  'not-assessed': 0,
  risk: 1,
  change: 2,
  protective: 3,
};

type Step =
  | { kind: 'welcome' }
  | { kind: 'pou'; pou: Pou }
  | { kind: 'members' };

type Props = {
  whanauId: number;
  profile: WhanauProfileResponse;
  onClose: () => void;
};

export function WhanauShareView({ whanauId, profile, onClose }: Props) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps: readonly Step[] = useMemo(
    () => [
      { kind: 'welcome' },
      ...POUS.map((p): Step => ({ kind: 'pou', pou: p })),
      { kind: 'members' },
    ],
    [],
  );
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // ESC + body scroll lock.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Browser back/forward intercept. Push a sentinel history state on mount;
  // when the user hits the device back button, popstate fires — we re-push
  // the sentinel (so they stay on the same URL) and route the dismiss
  // through onClose (which opens PIN verify in the parent).
  useEffect(() => {
    window.history.pushState({ wfpShareMode: true }, '');
    const onPop = () => {
      window.history.pushState({ wfpShareMode: true }, '');
      onClose();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, [onClose]);

  // Audit: log share_mode.entered on mount. Best-effort; failures don't
  // block the kaimahi.
  useEffect(() => {
    void callApi('/me/share-mode/event', {
      method: 'POST',
      body: JSON.stringify({ whanau_id: whanauId, event: 'entered' }),
      headers: { 'content-type': 'application/json' },
    }).catch(() => {
      // Audit failure must not interfere with sharing.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goPrev = () => {
    if (isFirstStep) return;
    setCurrentStepIndex((i) => i - 1);
  };
  const goNext = () => {
    if (isLastStep) return;
    setCurrentStepIndex((i) => i + 1);
  };

  const overlay = (
    <div
      className="share-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="share-modal">
        <header className="share-modal__top">
          <StepStrip
            steps={steps}
            currentStepIndex={currentStepIndex}
            onStepClick={(i) => setCurrentStepIndex(i)}
          />
          <button
            type="button"
            className="share-modal__close"
            onClick={onClose}
            aria-label={t('share.close')}
          >
            ×
          </button>
        </header>

        <section className="share-modal__body" aria-live="polite">
          {steps[currentStepIndex].kind === 'welcome' && (
            <WelcomeCard data={profile} />
          )}
          {steps[currentStepIndex].kind === 'pou' && (
            <PouCard
              pou={(steps[currentStepIndex] as { kind: 'pou'; pou: Pou }).pou}
              assessments={profile.assessments}
            />
          )}
          {steps[currentStepIndex].kind === 'members' && (
            <MembersCard members={profile.members} />
          )}
        </section>

        <nav className="share-modal__nav">
          <button
            type="button"
            className="share-modal__nav-button share-modal__nav-button--secondary"
            onClick={goPrev}
            disabled={isFirstStep}
          >
            ← {t('share.previous')}
          </button>
          <button
            type="button"
            className="share-modal__nav-button share-modal__nav-button--exit"
            onClick={onClose}
          >
            {t('share.handBack')}
          </button>
          <button
            type="button"
            className="share-modal__nav-button share-modal__nav-button--primary"
            onClick={goNext}
            disabled={isLastStep}
          >
            {t('share.next')} →
          </button>
        </nav>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

// ---------------------------------------------------------------------------
// Step strip — pill row, no numbers, te reo Pou names.
// ---------------------------------------------------------------------------

function StepStrip({
  steps,
  currentStepIndex,
  onStepClick,
}: {
  steps: readonly Step[];
  currentStepIndex: number;
  onStepClick: (i: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <ol className="share-strip" aria-label={t('share.sectionsLabel')}>
      {steps.map((step, i) => {
        const isCurrent = i === currentStepIndex;
        const label =
          step.kind === 'welcome'
            ? t('share.section.welcome')
            : step.kind === 'members'
              ? t('share.section.members')
              : step.pou.nameMi;
        return (
          <li
            key={
              step.kind === 'welcome'
                ? 'welcome'
                : step.kind === 'members'
                  ? 'members'
                  : step.pou.id
            }
            className={
              isCurrent
                ? 'share-strip__item share-strip__item--current'
                : 'share-strip__item'
            }
          >
            <button
              type="button"
              onClick={() => onStepClick(i)}
              aria-current={isCurrent ? 'step' : undefined}
              className="share-strip__button"
              lang={step.kind === 'pou' ? 'mi' : undefined}
            >
              {label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Welcome card — name + whakataukī + cycle statement.
// ---------------------------------------------------------------------------

function WelcomeCard({ data }: { data: WhanauProfileResponse }) {
  const { t } = useTranslation();
  return (
    <>
      <header className="share-view__header">
        <p className="share-view__welcome" lang="mi">
          {t('share.welcome', { name: data.whanau.display_name })}
        </p>
        <h1 id="share-title" className="share-view__name">
          {data.whanau.display_name}
        </h1>
      </header>

      {data.sessions.length > 0 ? (
        <p className="share-view__cycle-text">
          {t('share.cycle', {
            cycle: t(`wizard.cycle.${data.sessions[0].cycle_label}`, {
              defaultValue: data.sessions[0].cycle_label,
            }),
            date: formatDate(data.sessions[0].session_at),
            count: data.sessions.length,
          })}
        </p>
      ) : (
        <p className="share-view__cycle-text">{t('share.noSessionsYet')}</p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Pou card — one Pou, its subdomains, with trajectory.
// ---------------------------------------------------------------------------

function PouCard({
  pou,
  assessments,
}: {
  pou: Pou;
  assessments: SubdomainAssessmentRow[];
}) {
  const rows = assessments.filter((a) => a.pou_id === pou.id);
  return (
    <>
      <header className="share-view__pou-header">
        <h2 id="share-title" className="share-view__pou-name" lang="mi">
          {pou.nameMi}
        </h2>
        <p className="share-view__pou-description">{pou.description}</p>
      </header>

      <ul className="share-view__subdomain-list">
        {rows.map((a) => (
          <ShareFactorRow key={a.subdomain_id} assessment={a} />
        ))}
      </ul>
    </>
  );
}

// ---------------------------------------------------------------------------
// Members card.
// ---------------------------------------------------------------------------

function MembersCard({
  members,
}: {
  members: WhanauProfileResponse['members'];
}) {
  const { t } = useTranslation();
  return (
    <>
      <header className="share-view__members-header">
        <h2 id="share-title" className="share-view__section-title" lang="mi">
          {t('share.membersTitleMi')}
        </h2>
        <p className="share-view__section-subtitle">
          {t('share.membersSubtitle', { count: members.length })}
        </p>
      </header>

      {members.length === 0 ? (
        <p className="share-view__status">{t('share.noMembers')}</p>
      ) : (
        <ul className="share-view__member-list">
          {members.map((m) => (
            <li key={m.whanau_member_id} className="share-member">
              <p className="share-member__name">{m.display_name}</p>
              {m.relationship && (
                <p className="share-member__rel">{m.relationship}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// One subdomain row in the share view.
// ---------------------------------------------------------------------------

function ShareFactorRow({ assessment }: { assessment: SubdomainAssessmentRow }) {
  const { t } = useTranslation();
  const a = assessment;

  const stateLabel = (s: string | null): { mi: string; en: string } => {
    if (!s || s === 'not-assessed') {
      return { mi: '', en: t('share.notDiscussed') };
    }
    const teReo = t(`framework.state.${s}.teReo`, { defaultValue: '' });
    const en = t(`framework.state.${s}.descriptor`);
    return { mi: teReo, en };
  };

  const trajectory = (() => {
    if (!a.previous_state_kind || !a.state_kind) return null;
    if (a.previous_state_kind === a.state_kind) return 'unchanged' as const;
    const diff =
      (STATE_SCORE[a.state_kind] ?? 0) -
      (STATE_SCORE[a.previous_state_kind] ?? 0);
    return diff > 0 ? ('up' as const) : ('down' as const);
  })();

  const labels = stateLabel(a.state_kind);
  const subdomainName = t(`share.subdomain.${a.subdomain_id}`, {
    defaultValue: a.name_en,
  });

  return (
    <li className={`share-factor share-factor--${a.state_kind ?? 'unknown'}`}>
      <div className="share-factor__top">
        <div className="share-factor__main">
          <p className="share-factor__subdomain">{subdomainName}</p>
          <p className="share-factor__state">
            {a.state_kind && (
              <StateIcon
                state={a.state_kind}
                className="share-factor__state-icon"
              />
            )}
            {labels.mi && (
              <span className="share-factor__state-mi" lang="mi">
                {labels.mi}
              </span>
            )}
            <span className="share-factor__state-en">{labels.en}</span>
          </p>
        </div>

        {trajectory && (
          <div
            className={`share-factor__trajectory share-factor__trajectory--${trajectory}`}
          >
            <span className="share-factor__trajectory-icon" aria-hidden="true">
              {trajectory === 'up' ? '↑' : trajectory === 'down' ? '↓' : '→'}
            </span>
            <span className="share-factor__trajectory-label">
              {t(`share.trajectory.${trajectory}`)}
            </span>
          </div>
        )}
      </div>

      {a.shared_notes && (
        <blockquote className="share-factor__note">
          <p className="share-factor__note-label" lang="mi">
            {t('share.fromYourKaimahi')}
          </p>
          <p className="share-factor__note-body">{a.shared_notes}</p>
        </blockquote>
      )}
    </li>
  );
}

export default WhanauShareView;
